import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getErrorMessage } from '../lib/errors';
import { useAuthStore } from '../stores/authStore';
import CardNB from '../components/ui-nb/CardNB';
import { debug, error as logError } from '../lib/logger';

export default function AuthWebCallback() {
  const navigate = useNavigate();
  const signIn = useAuthStore(s => s.signIn);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const url = window.location.href;
      const hash = window.location.hash;
      const search = window.location.search;

      debug('Auth callback URL', { url });
      debug('Hash', { hash });

      // Check if this is a mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      debug('Is mobile', { isMobile });

      // Check for tokens in hash or search
      const hasTokens = (hash && hash.includes('access_token')) ||
                        (search && search.includes('access_token'));

      debug('Has tokens', { hasTokens });

      if (hasTokens && isMobile) {
        // Get the full URL with tokens
        const tokenUrl = url;

        // Convert to deep link with tokens
        // Replace https://domain with tellowup://
        let redirectUrl = tokenUrl.replace('https://tellowup.angeeeld.dev', 'tellowup://');

        // Also handle the hash properly
        if (hash) {
          redirectUrl = `tellowup://auth/callback${hash}`;
        }

        debug('Redirecting to mobile app', { redirectUrl });

        // Try to open the app
        window.location.href = redirectUrl;
        return;
      }

      // Web flow: wait for Supabase PKCE code exchange to complete
      // The SDK processes the ?code=xxx on page load asynchronously
      try {
        // Wait a moment for Supabase SDK to exchange the code
        await new Promise(resolve => setTimeout(resolve, 500));

        // Try to get session - may need retries for slow PKCE exchange
        let session = null;
        for (let i = 0; i < 5; i++) {
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            session = data.session;
            break;
          }
          debug(`[AuthWebCallback] Session not ready, retry ${i + 1}/5`);
          await new Promise(resolve => setTimeout(resolve, 800));
        }

        if (session) {
          debug('[AuthWebCallback] Session established', { userId: session.user.id });

          // Get or create user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          debug('[AuthWebCallback] Profile', { status: profile ? 'found' : 'creating' });

          if (!profile) {
            debug('[AuthWebCallback] Creating new profile');
            const { error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: session.user.id,
                email: session.user.email || '',
                full_name: session.user.user_metadata?.full_name || '',
                role: 'technician',
              });

            if (insertError) {
              logError('[AuthWebCallback] Profile insert failed', { error: getErrorMessage(insertError) });
            }
          }

          // Update Zustand auth store
          signIn({
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || '',
            role: profile?.role || 'technician',
            organizationId: profile?.organization_id || null,
            organizationName: null,
            provider: (session.user.app_metadata?.provider as 'google' | 'email') || 'email',
          });

          // Redirect based on profile state
          if (profile?.organization_id) {
            navigate('/en/admin');
          } else {
            navigate('/en/setup/role');
          }
        } else {
          logError('[AuthWebCallback] Failed to establish session after retries');
          setError('Authentication timed out. Please try again.');
        }
      } catch (err) {
        logError('[AuthWebCallback] Error', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Authentication failed');
      }
    };

    handleCallback();
  }, [navigate, signIn]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-nb-cream">
      <CardNB className="text-center max-w-md mx-auto p-8">
        <div className="w-12 h-12 border-3 border-nb-black border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-nb-text-secondary">Signing you in...</p>
      </CardNB>
    </div>
  );
}
